import { getJson, patchJson, postJson, requestVoid } from './client';

// --- Types (partner StudioController / StudioImageController) ---

export type StudioStatus =
  | 'DRAFT'
  | 'PENDING_ACTIVATION'
  | 'ACTIVE'
  | 'HIDDEN'
  | 'SUSPENDED';

export type StudioAddress = {
  roadAddress: string | null;
  detailAddress: string | null;
  latitude: number | null;
  longitude: number | null;
};

export type StudioParkingPolicy = {
  parkingType: string;
  parkingMethod: string | null;
  parkingFeeDescription: string | null;
};

export type StudioBusinessHour = {
  dayOfWeek: string;
  closed: boolean;
  openTime: string | null;
  closeTime: string | null;
};

export type StudioKeyword = {
  primaryCategory: string;
  secondaryKeyword: string;
  sortOrder?: number;
};

export type StudioGalleryImage = {
  galleryImageId: string;
  mediaId: string | null;
  imageUrl: string | null;
  sortOrder: number;
};

export type StudioDetail = {
  studioId: string;
  status: StudioStatus | string;
  name: string;
  slug: string | null;
  description: string | null;
  contactPhone: string | null;
  address: StudioAddress | null;
  parkingPolicy: StudioParkingPolicy | null;
  homepageUrls: string[];
  businessHours: StudioBusinessHour[];
  holidayNote: string | null;
  keywords: StudioKeyword[];
  primaryImageRef: string | null;
  primaryImageUrl: string | null;
  gallery: StudioGalleryImage[];
};

export type StudioCommandResponse = {
  studioId: string;
  status: StudioStatus | string;
};

export type StudioBasicInfoRequest = {
  slug?: string;
  name: string;
  description?: string;
  contactPhone?: string;
  address?: StudioAddress;
  parkingPolicy?: StudioParkingPolicy;
  homepageUrls?: string[];
};

export type StudioVisibilityAction = 'HIDE' | 'REQUEST_ACTIVATION';

// --- Studio CRUD ---

export function createStudio(req: StudioBasicInfoRequest) {
  return postJson<StudioCommandResponse>('/api/v1/studios', req);
}

export function updateStudioBasicInfo(studioId: string, req: StudioBasicInfoRequest) {
  return patchJson<StudioCommandResponse>(`/api/v1/studios/${encodeURIComponent(studioId)}`, req);
}

export function updateStudioVisibility(studioId: string, action: StudioVisibilityAction) {
  return patchJson<StudioCommandResponse>(
    `/api/v1/studios/${encodeURIComponent(studioId)}/visibility`,
    { action },
  );
}

export function getMyStudios() {
  return getJson<StudioDetail[]>('/api/v1/studios/me');
}

export function getStudioDetail(studioId: string) {
  return getJson<StudioDetail>(`/api/v1/studios/${encodeURIComponent(studioId)}`);
}

export function updateStudioBusinessHours(
  studioId: string,
  req: { businessHours: StudioBusinessHour[]; holidayNote?: string },
) {
  return requestVoid(`/api/v1/studios/${encodeURIComponent(studioId)}/business-hours`, {
    body: JSON.stringify(req),
    method: 'PUT',
  });
}

export function updateStudioKeywords(
  studioId: string,
  keywords: Array<Pick<StudioKeyword, 'primaryCategory' | 'secondaryKeyword'>>,
) {
  return requestVoid(`/api/v1/studios/${encodeURIComponent(studioId)}/keywords`, {
    body: JSON.stringify({ keywords }),
    method: 'PUT',
  });
}

// --- Studio images (OWNER, /api/v1/vendors/me/studios/**) ---

export type StudioImageTicket = {
  mediaId: string;
  imageUrl: string;
  ownershipTicket: string;
};

export function applyStudioPrimaryImage(studioId: string, req: StudioImageTicket) {
  return requestVoid(`/api/v1/vendors/me/studios/${encodeURIComponent(studioId)}/primary-image`, {
    body: JSON.stringify(req),
    method: 'PATCH',
  });
}

export function clearStudioPrimaryImage(studioId: string) {
  return requestVoid(`/api/v1/vendors/me/studios/${encodeURIComponent(studioId)}/primary-image`, {
    method: 'DELETE',
  });
}

export function addStudioGalleryImage(
  studioId: string,
  req: StudioImageTicket & { sortOrder?: number },
) {
  return postJson<StudioGalleryImage>(
    `/api/v1/vendors/me/studios/${encodeURIComponent(studioId)}/gallery`,
    req,
  );
}

export function removeStudioGalleryImage(studioId: string, galleryImageId: string) {
  return requestVoid(
    `/api/v1/vendors/me/studios/${encodeURIComponent(studioId)}/gallery/${encodeURIComponent(galleryImageId)}`,
    { method: 'DELETE' },
  );
}

export function reorderStudioGallery(studioId: string, galleryImageIds: string[]) {
  return requestVoid(`/api/v1/vendors/me/studios/${encodeURIComponent(studioId)}/gallery/order`, {
    body: JSON.stringify({ galleryImageIds }),
    method: 'PATCH',
  });
}
